import type { Permission } from "../types/api";

const moduleLabels: Record<string, string> = {
  admin: "Administration",
  ai: "AI tools",
  attendance: "Attendance",
  audit: "Audit trail",
  clients: "Clients",
  dashboard: "Dashboard",
  documents: "Documents",
  eap: "Employee support",
  employees: "People",
  finance: "Finance",
  leave: "Leave",
  programmes: "Programmes",
  reports: "Reports",
  roles: "Roles and access",
  settings: "Settings",
  system: "Technical tools",
  tools: "Office tools",
  tasks: "Tasks",
  tprs: "TPRS meetings",
  users: "Users",
  weekly_reports: "Weekly reports",
};

const permissionLabelOverrides: Record<string, string> = {
  "tprs.manage": "Upload and review own TPRS meeting recordings",
  "tprs.read.all": "Review all TPRS meeting records",
  "tprs.manage.all": "Manage other staff TPRS meeting records",
  "dashboard.management.read": "View company-wide dashboard metrics",
  "programmes.forms.read": "Review programme form submissions",
  "programmes.forms.manage": "Manage programme forms",
  "outreach.manage": "Prepare batch email campaigns",
  "outreach.send": "Send approved outreach emails",
  "ai.management.summary": "Generate management summaries",
  "ai.programme.recommendations": "Create programme recommendations",
  "clients.followup": "Record client follow-ups",
  "documents.sensitive": "Access confidential documents",
  "eap.sensitive": "Access sensitive employee support records",
  "system.technical": "Use technical tools",
  "tools.use": "Use office productivity tools",
};

const permissionResources: Record<string, string> = {
  "admin.assets": "assets",
  "admin.procurement": "procurement requests",
  "programmes.participants": "programme participants",
  weekly_reports: "weekly reports",
  employees: "staff records",
  clients: "clients",
  programmes: "programmes",
  documents: "documents",
  attendance: "attendance",
  finance: "finance",
  reports: "reports",
  settings: "settings",
  roles: "roles and access",
  tasks: "tasks",
  leave: "leave requests",
  eap: "employee support cases",
  users: "staff members",
  audit: "audit trail",
  dashboard: "dashboard",
  ai: "AI tools",
};

const permissionActionLabels: Record<string, string> = {
  "own.manage": "Manage my",
  "own.create": "Create my",
  "own.read": "View my",
  "own.update": "Update my",
  "read.all": "View all",
  manage: "Manage",
  read: "View",
  review: "Review",
  export: "Export",
  followup: "Record",
  sensitive: "Access sensitive",
  use: "Use",
  invite: "Invite",
  technical: "Use",
  recommendations: "Create",
  "management.summary": "Generate",
};

export function permissionLabel(
  permission: Pick<Permission, "code" | "description">,
) {
  const override = permissionLabelOverrides[permission.code];
  if (override) return override;

  if (permission.description) return permission.description.replace(/\.$/, "");

  const resourceKey = Object.keys(permissionResources)
    .sort((left, right) => right.length - left.length)
    .find(
      (key) => permission.code === key || permission.code.startsWith(`${key}.`),
    );
  if (!resourceKey)
    return permission.description?.replace(/\.$/, "") || "Allowed action";

  const action = permission.code.slice(resourceKey.length).replace(/^\./, "");
  const verb =
    permissionActionLabels[action] ||
    permission.description?.replace(/\.$/, "") ||
    "Manage";
  return `${verb} ${permissionResources[resourceKey]}`;
}

export function moduleLabel(module: string) {
  return moduleLabels[module] || module.replaceAll("_", " ");
}
